import React from 'react';
import {
    X, FileIcon, Download, FileText,
    Image as ImageIcon, FileBadge2, AlertCircle
} from 'lucide-react';

// --- FILE PREVIEW MODAL ---
const FilePreviewModal = ({ setPageIsLoading, pageIsLoading, isOpen, onClose, file }) => {
    if (!isOpen || !file) return null;

    // Normalize file type to ensure reliable matching
    const fileType = file?.fileType?.toLowerCase().replace('.', '');

    // Group file types
    const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType);
    const isPdf = fileType === 'pdf';
    // Microsoft Office Online Viewer supports these formats
    const isOfficeDoc = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType);

    const renderFile = () => {
        // 1. IMAGE RENDERER
        if (isImage) {
            return (
                <div className="relative w-full h-full flex items-center justify-center p-4 sm:p-8">
                    <img
                        src={file.fileUrl}
                        alt={file.name || 'Preview'}
                        className="max-w-full max-h-full rounded-2xl shadow-lg ring-1 ring-slate-200/50 object-contain bg-white"
                    />
                </div>
            );
        }

        // 2. PDF RENDERER
        if (isPdf) {
            return (
                <div className="w-full h-full p-2 sm:p-6">
                    <iframe
                        src={file.fileUrl}
                        className="w-full h-full rounded-2xl shadow-sm ring-1 ring-slate-200/60 bg-white"
                        title={file.name || 'PDF Preview'}
                    />
                </div>
            );
        }

        // 3. MICROSOFT OFFICE RENDERER (DOCX, XLSX, PPTX)
        if (isOfficeDoc) {
            const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.fileUrl)}`;
            return (
                <div className="w-full h-full p-2 sm:p-6 relative">
                    {/* Fallback overlay message in case URL is private/local and MS Viewer fails to load it */}
                    <div className="absolute top-8 left-0 right-0 text-center pointer-events-none opacity-50">
                        <p className="text-xs font-bold text-slate-400">Loading secure document viewer...</p>
                    </div>
                    <iframe
                        src={officeViewerUrl}
                        className="w-full h-full rounded-2xl shadow-sm ring-1 ring-slate-200/60 bg-white relative z-10"
                        title={file.name || 'Document Preview'}
                        frameBorder="0"
                    />
                </div>
            );
        }

        // 4. UNSUPPORTED FILE TYPE FALLBACK
        return (
            <div className="w-full h-full flex flex-col items-center justify-center p-6">
                <div className="bg-white p-10 rounded-3xl shadow-xl ring-1 ring-slate-200/60 max-w-md w-full text-center animate-in zoom-in-95 duration-300">
                    <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-indigo-50/50">
                        <FileIcon className="h-10 w-10 text-indigo-500" />
                    </div>

                    <h3 className="text-xl font-black text-slate-900 mb-2 truncate px-4" title={file.name}>
                        {file.name || 'Unknown File'}
                    </h3>

                    <div className="flex items-center justify-center gap-1.5 mb-6 text-amber-600 bg-amber-50 w-max mx-auto px-3 py-1.5 rounded-lg border border-amber-100">
                        <AlertCircle size={14} />
                        <span className="text-xs font-bold uppercase tracking-wider">Preview Not Available</span>
                    </div>

                    <p className="text-sm font-medium text-slate-500 mb-8 leading-relaxed">
                        This file format (<span className="font-bold text-slate-700">.{fileType}</span>) cannot be previewed securely in the browser. Please download it to view the contents.
                    </p>

                    <a
                        href={file.fileUrl}
                        download
                        target="_blank"
                        rel="noreferrer"
                        className="w-full inline-flex items-center justify-center px-6 py-3.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-800 shadow-md shadow-slate-900/10 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                    >
                        <Download className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                        Download File
                    </a>
                </div>
            </div>
        );
    };

    // Determine header icon based on type
    const HeaderIcon = isImage ? ImageIcon : isPdf ? FileBadge2 : isOfficeDoc ? FileText : FileIcon;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
            onClick={onClose}
        >
            {/* Glassmorphism Backdrop */}
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity"></div>

            {/* Modal Container */}
            <div
                className="bg-slate-50/80 w-full max-w-6xl h-[90vh] rounded-[2rem] shadow-2xl relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-[0.98] duration-300 ring-1 ring-white/20"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Premium Header */}
                <div className="px-6 py-4 border-b border-slate-200/50 bg-white/90 backdrop-blur-xl flex justify-between items-center shrink-0 z-20 shadow-sm">
                    <div className="flex items-center gap-4 min-w-0 pr-4">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl shrink-0">
                            <HeaderIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                            <h2 className="text-lg font-black text-slate-900 truncate" title={file.name}>
                                {file.name || 'Document Preview'}
                            </h2>
                            {/* {fileType && (
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                    {fileType} Document
                                </span>
                            )} */}
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {/* Direct Download Button in Header */}
                        {/* <a 
                            href={file.fileUrl} 
                            download 
                            target="_blank" 
                            rel="noreferrer"
                            className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors ring-1 ring-indigo-200/50"
                        >
                            <Download size={16} />
                            Download
                        </a> */}
                        <button
                            onClick={onClose}
                            aria-label="Close preview"
                            className="p-2 bg-white rounded-full hover:bg-rose-50 hover:text-rose-600 text-slate-400 shadow-sm transition-colors border border-slate-200">
                            <X size={20} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* Dynamic Content Area */}
                <div className="flex-1 overflow-hidden bg-slate-100/50 relative">
                    {/* Subtle grid background pattern for contrast */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, black 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>

                    <div className="relative w-full h-full z-10">
                        {renderFile()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;